import { action } from 'typesafe-actions';
import {
  ICalculatorPaymentSet,
  ICalculatorSettingsOverride,
  ICreateEmployeeSite,
  IEmployee,
  IEmployeeIntegration,
  IEmployeePayrollTip,
  IExportCalculatorSettings,
  IPOSProvider,
  IPOSResults,
  ISite,
  ITipCalculatorChangeLogEntry,
  ITipCalculatorPayee,
  ITipCalculatorRequest,
  ITipCalculatorResult,
} from 'types';
import * as ActionTypes from './types';

// Manage Tip Calculator Payments
export const resetCalculatorPaymentSet = () => action(ActionTypes.TipCalculator.RESET_CALCULATOR_PAYMENTSET);
export const updateCalculatorPaymentset = (paymentSet: ICalculatorPaymentSet) => action(ActionTypes.TipCalculator.UPDATE_CALCULATOR_PAYMENTSET_REQUEST, { paymentSet });

// Get pos data
export const getPOSDataRequest = (siteId: string, date: string, onSuccess?: (data: IPOSResults) => void, onError?: (error: any) => void, skipNoLabor?: boolean, settingsOverride?: ICalculatorSettingsOverride) =>
  action(ActionTypes.TipCalculator.GET_POS_REQUEST, { siteId, date, onSuccess, onError, skipNoLabor, settingsOverride });
export const getPOSDataSuccess = (response: IPOSResults) => action(ActionTypes.TipCalculator.GET_POS_SUCCESS, { response });
export const getPOSDataError = (error: any) => action(ActionTypes.TipCalculator.GET_POS_FAIL, { error });

// Calculate Tips
export const getCalculatedTipsRequest = (tipCalcRequests: ITipCalculatorRequest[], posResults: IPOSResults, creditCardFeePercent: number, onSuccess: (data: ITipCalculatorResult[]) => void, onError: (error: any) => void) =>
  action(ActionTypes.TipCalculator.GET_CALCULATED_TIPS_REQUEST, { tipCalcRequests, posResults, creditCardFeePercent, onSuccess, onError });
export const getCalculatedTipsSuccess = (response: ITipCalculatorResult[]) => action(ActionTypes.TipCalculator.GET_CALCULATED_TIPS_SUCCESS, { response });
export const getCalculatedTipsError = (error: any) => action(ActionTypes.TipCalculator.GET_CALCULATED_TIPS_FAIL, { error });
export const updateCalculatedTipsResults = (results: ITipCalculatorResult[]) => action(ActionTypes.TipCalculator.UPDATE_CALCULATED_TIPS_RESULTS, { results });

export const setCalculatorPayees = (payees: ITipCalculatorPayee[]) => action(ActionTypes.TipCalculator.SET_CALCULATOR_PAYEES, { payees });
export const setPOSResults = (data: IPOSResults) => action(ActionTypes.TipCalculator.SET_POS_RESULTS, { data });

// Update payee
export const updatePayeeHoldback = (payee: ITipCalculatorPayee, holdback: number) => action(ActionTypes.TipCalculator.UPDATE_PAYEE_HOLDBACK, { payee, holdback });
export const updatePayeeNote = (payeeId: string, note: string) => action(ActionTypes.TipCalculator.UPDATE_PAYEE_NOTE, { payeeId, note });
export const connectEmployeeToPayee = (employee: IEmployee, site: ISite, payrollTips?: IEmployeePayrollTip[]) => action(ActionTypes.TipCalculator.CONNECT_EMPLOYEE_TO_PAYEE, { employee, site, payrollTips });

// Link Employee To Integration
export const linkEmployeeRequest = (employee: IEmployee, integration: IEmployeeIntegration, site: ICreateEmployeeSite, onSuccess: (employee: IEmployee) => void, onError: (error: any) => void) =>
  action(ActionTypes.TipCalculator.LINK_EMPLOYEE_REQUEST, { employee, integration, site, onSuccess, onError });
export const linkEmployeeSuccess = (response: IEmployee) => action(ActionTypes.TipCalculator.LINK_EMPLOYEE_SUCCESS, { response });
export const linkEmployeeError = (errors: any) => action(ActionTypes.TipCalculator.LINK_EMPLOYEE_FAIL, { errors }, { failToast: true });

// Unlink Employee from Integration
export const unlinkEmployeeRequest = (employee: IEmployee, integration: IEmployeeIntegration, onSuccess: (employee: IEmployee) => void) => action(ActionTypes.TipCalculator.UNLINK_EMPLOYEE_REQUEST, { employee, integration, onSuccess });
export const unlinkEmployeeSuccess = (response: IEmployee) => action(ActionTypes.TipCalculator.UNLINK_EMPLOYEE_SUCCESS, response);
export const unlinkEmployeeError = (errors: any) => action(ActionTypes.TipCalculator.UNLINK_EMPLOYEE_FAIL, { errors }, { failToast: true });

// Find Matching Employees for linking
export const getMatchEmployeesRequest = (siteId: string, provider: IPOSProvider, onSuccess: (employees: IEmployee[]) => void, onDuplicateProviderMatch: (employee: IEmployee) => void, onError: (error: any) => void) =>
  action(ActionTypes.TipCalculator.GET_MATCH_EMPLOYEES_REQUEST, { siteId, provider, onSuccess, onDuplicateProviderMatch, onError });
export const getMatchEmployeesSuccess = (response: IEmployee[]) => action(ActionTypes.TipCalculator.GET_MATCH_EMPLOYEES_SUCCESS, { response });
export const getMatchEmployeesError = (errors: any) => action(ActionTypes.TipCalculator.GET_MATCH_EMPLOYEES_FAIL, { errors });

// Export Calculator Settings
export const exportCalculatorSettingsRequest = (siteId: string, onSuccess: (data: IExportCalculatorSettings) => void) => action(ActionTypes.TipCalculator.EXPORT_CALCULATOR_SETTINGS_REQUEST, { siteId, onSuccess });
export const exportCalculatorSettingsSuccess = (response: IExportCalculatorSettings) => action(ActionTypes.TipCalculator.EXPORT_CALCULATOR_SETTINGS_SUCCESS, response);
export const exportCalculatorSettingsError = (error: any) => action(ActionTypes.TipCalculator.EXPORT_CALCULATOR_SETTINGS_FAIL, error, { failToast: true });

// Import Calculator Settings
export const importCalculatorSettingsRequest = (siteId: string, body: IExportCalculatorSettings, onSuccess: () => void, onError: () => void) =>
  action(ActionTypes.TipCalculator.IMPORT_CALCULATOR_SETTINGS_REQUEST, { siteId, body, onSuccess, onError });
export const importCalculatorSettingsSuccess = (response: IExportCalculatorSettings) => action(ActionTypes.TipCalculator.IMPORT_CALCULATOR_SETTINGS_SUCCESS, response);
export const importCalculatorSettingsError = (error: any) => action(ActionTypes.TipCalculator.IMPORT_CALCULATOR_SETTINGS_FAIL, error);

// Set Change log
export const setChangeLogEntries = (entries: ITipCalculatorChangeLogEntry[]) => action(ActionTypes.TipCalculator.SET_CHANGE_LOG_ENTRIES, { entries });
